import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState, useRef } from 'react';
import { Stack, Typography, CircularProgress, Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import type { FormikErrors, FormikTouched } from 'formik';
import * as Yup from 'yup';
import { keepPreviousData } from '@tanstack/react-query';
import { ClientError } from '@pflegenavi/shared-frontend/platform';
import { CountrySelector } from '@pflegenavi/web-components';
import { DynamicRecipientForm } from '../DynamicRecipientForm';
import {
  useGetRecipientSchema,
  useGetPayoutMethods,
  useValidateRecipientData,
  useGetAllCountriesPhoenix,
  useSortedCountries,
  transformFlatErrorsToNested,
  filterErrorsByTouched,
  type RecipientData,
  type RecipientSchemaParams,
} from '@pflegenavi/service-provider-frontend/api';

interface ValidationError {
  field: string;
  message: string;
}

interface ValidationErrorResponse extends ClientError {
  error: string;
  errors: ValidationError[];
}

interface MangopayConnectFormStep3Props {
  disabled: boolean;
  onSchemaLoaded?: (params: RecipientSchemaParams) => void;
  onDataChange?: (data: RecipientData) => void;
  onValidationChange?: (isValid: boolean) => void;
  recipientType: 'Individual' | 'Business';
  serviceProviderCountryId?: string;
  initialValues?: Partial<RecipientData>;
}

export const MangopayConnectFormStep3: FC<MangopayConnectFormStep3Props> = ({
  disabled,
  onSchemaLoaded,
  onDataChange,
  onValidationChange,
  recipientType,
  serviceProviderCountryId,
  initialValues,
}) => {
  const { t } = useTranslation();
  const selectedPayoutMethod = 'LocalBankTransfer';
  const [isAsyncValidating, setIsAsyncValidating] = useState(false);
  // Changing the destination clears the bank fields without revalidating
  // (validateOnChange is off), so the form is not valid again until the next
  // blur-triggered validation has run.
  const [needsValidation, setNeedsValidation] = useState(false);

  // Track touched state for error filtering
  const touchedRef = useRef<FormikTouched<RecipientData>>({});

  // Track unfiltered errors for validation logic (separate from display)
  const unfilteredErrorsRef = useRef<FormikErrors<RecipientData>>({});

  // Fetch countries (also used to resolve the SP's country UUID to an ISO code)
  const { data: countriesData, isLoading: isLoadingCountries } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });
  const countries = useSortedCountries(countriesData ?? []);

  // MangoPay wants ISO codes; the SP profile stores a country UUID.
  const serviceProviderCountryIso = useMemo(
    () => countries.find((c) => c.id === serviceProviderCountryId)?.isoCode,
    [countries, serviceProviderCountryId]
  );

  // Payout destination. Defaults to the SP's own country and EUR, but a provider
  // registered in AT may still bank in HU.
  const [destinationCountry, setDestinationCountry] = useState<string>(
    initialValues?.Country ?? ''
  );

  useEffect(() => {
    if (!destinationCountry && serviceProviderCountryIso) {
      setDestinationCountry(serviceProviderCountryIso);
    }
  }, [destinationCountry, serviceProviderCountryIso]);

  // Fetch available payout methods for the chosen destination
  const {
    data: payoutMethods,
    isLoading: isLoadingPayoutMethods,
    error: payoutMethodsError,
  } = useGetPayoutMethods(
    { currency: 'EUR', country: destinationCountry || undefined },
    !!destinationCountry
  );

  const localBankTransferAvailable =
    !!payoutMethods?.includes(selectedPayoutMethod);

  const schemaParams = useMemo<RecipientSchemaParams>(
    () => ({
      currency: 'EUR',
      payout_method_type: selectedPayoutMethod,
      country: destinationCountry || undefined,
    }),
    [selectedPayoutMethod, destinationCountry]
  );

  const {
    data: schema,
    isLoading: isLoadingSchema,
    error: schemaError,
  } = useGetRecipientSchema(
    schemaParams,
    localBankTransferAvailable && !!destinationCountry
  );

  const { mutateAsync: validateRecipientData } = useValidateRecipientData();

  // Extract country from schema's AllowedValues if available
  const countryField = schema?.Country;
  const defaultCountry =
    countryField &&
    typeof countryField === 'object' &&
    'AllowedValues' in countryField &&
    Array.isArray(countryField.AllowedValues) &&
    countryField.AllowedValues.length > 0
      ? countryField.AllowedValues[0]
      : '';

  // Create a separate formik instance for the dynamic recipient form
  const recipientFormik = useFormik<RecipientData>({
    initialValues: {
      DisplayName: '',
      PayoutMethodType: 'LocalBankTransfer',
      RecipientType: recipientType,
      Currency: 'EUR',
      Country: defaultCountry,
      ...initialValues,
    },
    onSubmit: () => {
      // Not used, parent form handles submission
    },
    validationSchema: Yup.object(), // Disable Yup validation since we're using async validation
    validate: async (values: RecipientData) => {
      // Only validate with API if we have the required params
      if (!schemaParams || !selectedPayoutMethod) {
        return {};
      }

      setIsAsyncValidating(true);
      try {
        const response = await validateRecipientData({
          ...schemaParams,
          recipient_data: values,
        });

        // If validation passes, return no errors
        if (response.valid) {
          return {};
        }
      } catch (error: unknown) {
        // Handle validation errors from the API
        if (error instanceof ClientError) {
          const validationError = error as ValidationErrorResponse;

          if (validationError.errors && Array.isArray(validationError.errors)) {
            // First, translate all error messages
            const translatedErrors = validationError.errors.map(
              (err: ValidationError) => {
                const translationKey = `mangopay.recipient.errors.${err.message}`;
                const translatedMessage = t(translationKey);

                return {
                  field: err.field,
                  message:
                    translatedMessage !== translationKey
                      ? translatedMessage
                      : err.message,
                };
              }
            );

            // Transform flat errors to nested structure
            const nestedErrors = transformFlatErrorsToNested(translatedErrors);

            // Store unfiltered errors for validation logic
            unfilteredErrorsRef.current = nestedErrors;

            // Filter errors to only show errors for touched fields
            const filteredErrors = filterErrorsByTouched(
              nestedErrors,
              touchedRef.current
            );
            return filteredErrors;
          }
        }
      } finally {
        setIsAsyncValidating(false);
        setNeedsValidation(false);
      }

      // Return empty errors if something unexpected happens
      return {};
    },
    validateOnChange: false, // Only validate on blur, not on every change
    validateOnBlur: true,
  });

  const { values, errors, touched, isValid, setFieldValue, setFieldError } =
    recipientFormik;

  // Update touched ref whenever touched state changes
  useEffect(() => {
    touchedRef.current = touched;
  }, [touched]);

  // A new destination means a new schema (e.g. IBAN vs. AccountNumber), so bank
  // details entered for the previous one are dropped.
  const changeDestination = (country: string) => {
    setDestinationCountry(country);
    setNeedsValidation(true);
    setFieldValue('LocalBankTransfer', undefined);
    setFieldValue('InternationalBankTransfer', undefined);
    setFieldError('LocalBankTransfer', undefined);
    setFieldError('InternationalBankTransfer', undefined);
  };

  // Update Country field when schema loads or changes
  useEffect(() => {
    const countryField = schema?.Country;
    const countryFromSchema =
      countryField &&
      typeof countryField === 'object' &&
      'AllowedValues' in countryField &&
      Array.isArray(countryField.AllowedValues) &&
      countryField.AllowedValues.length > 0
        ? countryField.AllowedValues[0]
        : undefined;
    if (countryFromSchema && values.Country !== countryFromSchema) {
      setFieldValue('Country', countryFromSchema);
    }
  }, [schema, setFieldValue, values.Country]);

  // Notify parent when schema is loaded
  useEffect(() => {
    if (schema && onSchemaLoaded && selectedPayoutMethod) {
      onSchemaLoaded(schemaParams);
    }
  }, [schema, schemaParams, onSchemaLoaded, selectedPayoutMethod]);

  // Watch form changes and notify parent
  useEffect(() => {
    if (onDataChange) {
      onDataChange(values);
    }
  }, [values, onDataChange]);

  // Watch validation state and notify parent
  useEffect(() => {
    if (onValidationChange) {
      // Check if form is valid and has been touched
      const hasErrors = Object.keys(errors).length > 0;
      const touchedCount = Object.keys(touched).length;
      const formIsValid =
        isValid &&
        touchedCount > 0 &&
        !hasErrors &&
        !isAsyncValidating &&
        !needsValidation;
      onValidationChange(formIsValid);
    }
  }, [
    isValid,
    touched,
    errors,
    isAsyncValidating,
    needsValidation,
    onValidationChange,
  ]);

  // Selector values are ISO codes, not the SP's country UUIDs. Memoized:
  // CountrySelector re-syncs its state whenever this array changes identity.
  const destinationCountryOptions = useMemo(
    () => countries.map((c) => ({ value: c.isoCode, label: c.label })),
    [countries]
  );

  if (isLoadingCountries) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Always on top: changing it reloads the whole form below.
  const destinationSelectors = (
    <Box>
      <CountrySelector
        countries={destinationCountryOptions}
        countryId={destinationCountry}
        onChangeCountry={changeDestination}
        label={t('service-provider-app.connect-account.destination-country')}
        disabled={disabled}
        fullWidth
        data-testid="mangopay-recipient-destination-country"
      />
      <Typography
        variant="caption"
        sx={{ color: 'grey.500', display: 'block', mb: 1 }}
      >
        {t('service-provider-app.connect-account.destination-country-hint')}
      </Typography>
    </Box>
  );

  let body: ReactNode = null;
  if (isLoadingPayoutMethods || isLoadingSchema) {
    body = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (payoutMethodsError || schemaError) {
    body = (
      <Alert severity="error">
        {t('service-provider-app.connect-account.schema-load-error')}
      </Alert>
    );
  } else if (destinationCountry && !localBankTransferAvailable) {
    body = (
      <Alert severity="warning">
        {t('service-provider-app.connect-account.no-payout-methods')}
      </Alert>
    );
  }

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography
        sx={{
          color: 'grey.500',
        }}
      >
        {t('service-provider-app.connect-account.bank-account-details')}
      </Typography>

      {destinationSelectors}

      {body ??
        (schema && (
          <Box component="form" onSubmit={(e) => e.preventDefault()}>
            <DynamicRecipientForm
              schema={schema}
              formik={recipientFormik}
              countryOptions={countries}
              disabled={disabled}
            />
          </Box>
        ))}
    </Stack>
  );
};
